import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { naira } from "../../lib/format";
import { HC } from "./theme";
import { Card, SectionHead, Btn, Table, Field, Input, Select } from "./ui";
import { toast } from "sonner";
import { Plus, Building2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";

export default function Departments() {
  const [deps, setDeps] = useState([]);
  const [poss, setPoss] = useState([]);
  const [depForm, setDepForm] = useState(null);
  const [posForm, setPosForm] = useState(null);

  const load = () => {
    api.get("/hr/departments").then(setDeps).catch((e) => toast.error(formatApiError(e)));
    api.get("/hr/positions").then(setPoss).catch(() => {});
  };
  useEffect(load, []);

  const saveDep = async () => {
    try {
      if (depForm.id) await api.put(`/hr/departments/${depForm.id}`, depForm);
      else await api.post("/hr/departments", depForm);
      toast.success("Department saved"); setDepForm(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const savePos = async () => {
    try {
      if (posForm.id) await api.put(`/hr/positions/${posForm.id}`, posForm);
      else await api.post("/hr/positions", posForm);
      toast.success("Position saved"); setPosForm(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="hr-departments-page">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card>
          <SectionHead title={`Departments (${deps.length})`} sub="Configurable — nothing is hardcoded"
            right={<Btn className="h-9" onClick={() => setDepForm({ name: "", code: "", description: "" })} data-testid="hr-add-department"><Plus size={15} /> New</Btn>} />
          <Table
            testid="hr-departments-table" rows={deps} empty="No departments"
            onRowClick={(r) => setDepForm(r)}
            columns={[
              { key: "name", label: "DEPARTMENT", render: (r) => (
                  <span className="flex items-center gap-2"><Building2 size={15} style={{ color: HC.gold }} /><span className="font-semibold">{r.name}</span></span>) },
              { key: "code", label: "CODE" },
              { key: "position_count", label: "POSITIONS", align: "right" },
              { key: "employee_count", label: "EMPLOYEES", align: "right" },
            ]}
          />
        </Card>

        <Card>
          <SectionHead title={`Positions (${poss.length})`} sub="Titles, levels and default basic salary"
            right={<Btn className="h-9" onClick={() => setPosForm({ title: "", department_id: "", level: "", default_basic: 0 })} data-testid="hr-add-position"><Plus size={15} /> New</Btn>} />
          <Table
            testid="hr-positions-table" rows={poss} empty="No positions" onRowClick={(r) => setPosForm(r)}
            columns={[
              { key: "title", label: "POSITION", render: (r) => <span className="font-semibold">{r.title}</span> },
              { key: "department_name", label: "DEPARTMENT" },
              { key: "level", label: "LEVEL" },
              { key: "default_basic", label: "DEFAULT BASIC", align: "right", render: (r) => <span className="tabular-nums">{naira(r.default_basic)}</span> },
              { key: "employee_count", label: "STAFF", align: "right" },
            ]}
          />
        </Card>
      </div>

      <Dialog open={!!depForm} onOpenChange={(o) => !o && setDepForm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{depForm?.id ? "Edit department" : "New department"}</DialogTitle>
            <DialogDescription>Departments scope headcount, payroll cost and AI insights.</DialogDescription>
          </DialogHeader>
          {depForm && (
            <div className="space-y-3">
              <Field label="Name"><Input data-testid="dept-name" value={depForm.name} onChange={(e) => setDepForm({ ...depForm, name: e.target.value })} /></Field>
              <Field label="Code"><Input value={depForm.code || ""} onChange={(e) => setDepForm({ ...depForm, code: e.target.value })} /></Field>
              <Field label="Description"><Input value={depForm.description || ""} onChange={(e) => setDepForm({ ...depForm, description: e.target.value })} /></Field>
            </div>
          )}
          <DialogFooter>
            <Btn variant="outline" onClick={() => setDepForm(null)}>Cancel</Btn>
            <Btn onClick={saveDep} data-testid="dept-save">Save</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!posForm} onOpenChange={(o) => !o && setPosForm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{posForm?.id ? "Edit position" : "New position"}</DialogTitle>
            <DialogDescription>Default basic salary pre-fills new employee records.</DialogDescription>
          </DialogHeader>
          {posForm && (
            <div className="space-y-3">
              <Field label="Title"><Input data-testid="pos-title" value={posForm.title} onChange={(e) => setPosForm({ ...posForm, title: e.target.value })} /></Field>
              <Field label="Department">
                <Select data-testid="pos-department" value={posForm.department_id || ""} onChange={(e) => setPosForm({ ...posForm, department_id: e.target.value })}>
                  <option value="">Select department</option>
                  {deps.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </Select>
              </Field>
              <Field label="Level"><Input value={posForm.level || ""} onChange={(e) => setPosForm({ ...posForm, level: e.target.value })} /></Field>
              <Field label="Default basic (monthly)">
                <Input type="number" value={posForm.default_basic || 0} onChange={(e) => setPosForm({ ...posForm, default_basic: Number(e.target.value) })} />
              </Field>
            </div>
          )}
          <DialogFooter>
            <Btn variant="outline" onClick={() => setPosForm(null)}>Cancel</Btn>
            <Btn onClick={savePos} data-testid="pos-save">Save</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
